import { useState, useEffect } from 'preact/hooks';
import emitter from '@webcore/core/events/eventemitter';
import appEvents from '@core/events/events';
import { PlacementOverlay } from './PlacementOverlay';

import soundUnmuted from '@assets-shared/icons/sound-unmuted.svg';
import soundMuted from '@assets-shared/icons/sound-muted.svg';

interface AROverlayProps {
  onExit: () => void;
  isRecording?: boolean;
}

export function AROverlay({ onExit, isRecording = false }: AROverlayProps) {
  // Initialize previewVisible to true since AROverlay only shows after model is loaded
  const [previewVisible, setPreviewVisible] = useState(true);
  const [objectPlaced, setObjectPlaced] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    const handlePreviewVisible = () => {
      setPreviewVisible(true);
    };

    const handleContentReady = () => {
      setObjectPlaced(true);
    };

    const handleRepositioning = () => {
      // Reset states when repositioning - show placement overlay again
      setObjectPlaced(false);
      setPreviewVisible(true);
    };

    const handleMutedChange = (muted: boolean) => {
      setIsMuted(muted);
    };

    emitter.on('content-loading', handlePreviewVisible);
    emitter.on('content-ready', handleContentReady);
    (emitter as any).on('content-repositioning', handleRepositioning);
    appEvents.on('video-muted-change', handleMutedChange);

    return () => {
      emitter.off('content-loading', handlePreviewVisible);
      emitter.off('content-ready', handleContentReady);
      (emitter as any).off('content-repositioning', handleRepositioning);
      appEvents.off('video-muted-change', handleMutedChange);
    };
  }, []);

  const handleMuteToggle = () => {
    const next = !isMuted;
    // Optimistic update so the icon flips immediately; video-muted-change will reconcile.
    setIsMuted(next);
    appEvents.emit('video-mute-toggle', next);
  };

  const handleTapToPlace = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    if (previewVisible && !objectPlaced) {
      // Set objectPlaced immediately to prevent double-tap
      setObjectPlaced(true);
      appEvents.emit('content-positioned');
    }
  };

  const handleReposition = () => {
    // Emit reposition - main.app.ts will handle the state reset
    // and emit 'content-repositioning' which we listen for
    (emitter as any).emit('reposition');
  };

  return (
    <div className="absolute inset-0" style={{ pointerEvents: 'none' }}>
      {/* Full screen tap target for placing */}
      {previewVisible && !objectPlaced && (
        <div 
          className="absolute inset-0"
          onClick={handleTapToPlace}
          style={{ touchAction: 'none', pointerEvents: 'auto' }}
        />
      )}
      
      {/* Tap to place indicator */}
      {previewVisible && !objectPlaced && (
        <PlacementOverlay />
      )}

      {/* Close button - Top Left (hidden when recording) */}
      {!isRecording && (
        <div 
          className="absolute"
          style={{ 
            pointerEvents: 'auto', 
            top: 'calc(env(safe-area-inset-top, 0px) + 40px)',
            left: '20px'
          }}
        >
          <button
            onClick={onExit}
            className="flex justify-center items-center hover:opacity-80"
            aria-label="Close AR experience"
          >
            <svg width="50" height="50" viewBox="0 0 50 50" fill="none">
              <circle cx="25" cy="25" r="25" fill="black" fillOpacity="0.5" />
              <circle cx="25" cy="25" r="23.5" stroke="white" strokeWidth="2" />
              <path
                d="M22.9287 25.0055L16.4062 18.4789L18.4886 16.4062L25 22.9218L31.5224 16.4062L33.5937 18.4789L27.0823 25.0055L33.5937 31.5211L31.5224 33.5937L25 27.0782L18.4886 33.5937L16.4173 31.5211L22.9287 25.0055Z"
                fill="white"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Top-right button stack: mute (always in AR), reposition (when placed). Hidden when recording. */}
      {!isRecording && (
        <div
          className="absolute flex flex-col gap-3"
          style={{
            pointerEvents: 'auto',
            top: 'calc(env(safe-area-inset-top, 0px) + 40px)',
            right: '20px',
          }}
        >
          <button
            onClick={handleMuteToggle}
            className="flex justify-center items-center hover:opacity-80"
            aria-label={isMuted ? 'Unmute audio' : 'Mute audio'}
            aria-pressed={isMuted}
          >
            <img
              src={isMuted ? soundMuted : soundUnmuted}
              alt=""
              width={54}
              height={54}
              draggable={false}
            />
          </button>

          {objectPlaced && (
          <button
            onClick={handleReposition}
            className="flex justify-center items-center hover:opacity-80"
            aria-label="Reposition object"
          >
            <svg width="54" height="54" viewBox="0 0 54 54" fill="none" xmlns="http://www.w3.org/2000/svg">
              <mask id="mask0_2835_2761" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="54" height="54">
                <circle cx="27" cy="27" r="27" fill="#D9D9D9"/>
              </mask>
              <g mask="url(#mask0_2835_2761)">
                <rect x="-40" width="134" height="53.9089" fill="black" fillOpacity="0.5"/>
              </g>
              <g filter="url(#filter0_i_2835_2761)">
                <circle cx="27" cy="27" r="27" fill="url(#paint0_linear_2835_2761)" fillOpacity="0.1"/>
              </g>
              <circle cx="27" cy="27" r="26" stroke="white" strokeWidth="2"/>
              <g filter="url(#filter1_d_2835_2761)">
                <path d="M31.6668 16.6667L27.0002 12L22.3335 16.6667" stroke="white" strokeWidth="2" strokeLinecap="square"/>
                <path d="M22.3332 37.3333L26.9998 42L31.6665 37.3333" stroke="white" strokeWidth="2" strokeLinecap="square"/>
                <path d="M37.3333 31.6667L42 27L37.3333 22.3334" stroke="white" strokeWidth="2" strokeLinecap="square"/>
                <path d="M16.6667 22.3333L12 27L16.6667 31.6666" stroke="white" strokeWidth="2" strokeLinecap="square"/>
                <circle cx="27" cy="27" r="2" fill="white"/>
                <path d="M28 40.416C28 40.9683 27.5523 41.416 27 41.416C26.4477 41.416 26 40.9683 26 40.416V32.9061C26 32.3859 26.4797 32 27 32C27.5203 32 28 32.3859 28 32.9061V40.416ZM40.416 26C40.9683 26 41.416 26.4477 41.416 27C41.416 27.5523 40.9683 28 40.416 28H32.9061C32.3859 28 32 27.5203 32 27C32 26.4797 32.3859 26 32.9061 26H40.416ZM21.0939 26C21.6141 26 22 26.4797 22 27C22 27.5203 21.6141 28 21.0939 28H13.583C13.0307 28 12.583 27.5523 12.583 27C12.583 26.4477 13.0307 26 13.583 26H21.0939ZM28 21.093C28 21.6132 27.5202 22 27 22C26.4798 22 26 21.6132 26 21.093V13.583C26 13.0307 26.4477 12.583 27 12.583C27.5523 12.583 28 13.0307 28 13.583V21.093Z" fill="white"/>
              </g>
              <defs>
                <filter id="filter0_i_2835_2761" x="0" y="0" width="54" height="54" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                  <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                  <feOffset/>
                  <feGaussianBlur stdDeviation="5"/>
                  <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.5 0"/>
                  <feBlend mode="normal" in2="shape" result="effect1_innerShadow_2835_2761"/>
                </filter>
                <filter id="filter1_d_2835_2761" x="6.58594" y="10.5858" width="40.8281" height="40.8284" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                  <feOffset dy="4"/>
                  <feGaussianBlur stdDeviation="2"/>
                  <feComposite in2="hardAlpha" operator="out"/>
                  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2835_2761"/>
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2835_2761" result="shape"/>
                </filter>
                <linearGradient id="paint0_linear_2835_2761" x1="27" y1="0" x2="27" y2="54" gradientUnits="userSpaceOnUse">
                  <stop stopOpacity="0"/>
                  <stop offset="1"/>
                </linearGradient>
              </defs>
            </svg>
          </button>
          )}
        </div>
      )}

    </div>
  );
}
